import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const COMMODITY_OPTIONS = [
  { v: "crude",   l: "Crude Oil (WTI / Brent)" },
  { v: "refined", l: "Refined Petroleum (EN 590 / Jet A-1)" },
  { v: "sugar",   l: "Sugar — ICUMSA 45" },
  { v: "coffee",  l: "Coffee — Arabica / Robusta" },
];

const PAYMENT_OPTIONS = ["DLC", "SBLC", "MT103", "Escrow"];

function Toggle({ value, set, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.v;
        const l = typeof o === "string" ? o : o.l;
        const active = value.includes(v);
        return (
          <button type="button" key={v}
                  onClick={() => set(active ? value.filter((x) => x !== v) : [...value, v])}
                  className={`text-xs font-mono uppercase tracking-wider px-3 py-2 border transition-colors ${
                    active ? "bg-oxblood text-parchment border-oxblood" : "border-line text-sub hover:text-ink"
                  }`}
                  data-testid={`${testid}-${v}`}>
            {l}
          </button>
        );
      })}
    </div>
  );
}

export default function SupplierProfile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    commodities: [], origins: "", monthly_capacity: "", quality_specs: "",
    payment_terms_accepted: [], reference_cargoes: "", banking_institution: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const sp = user?.supplier_profile;
    if (sp) {
      setForm({
        commodities: sp.commodities || [],
        origins: sp.origins || "",
        monthly_capacity: sp.monthly_capacity || "",
        quality_specs: sp.quality_specs || "",
        payment_terms_accepted: sp.payment_terms_accepted || [],
        reference_cargoes: sp.reference_cargoes || "",
        banking_institution: sp.banking_institution || "",
      });
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (!form.commodities.length) throw new Error("Pick at least one commodity");
      if (!form.payment_terms_accepted.length) throw new Error("Pick at least one payment term");
      await api.patch("/auth/me/supplier-profile", form);
      await refresh();
      nav("/dashboard/supplier-agreements");
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Submit failed");
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout testid="supplier-profile-page">
      <form onSubmit={submit} className="p-8 md:p-12 max-w-3xl">
        <div className="overline">Supplier desk · step 03</div>
        <h1 className="h-serif text-5xl text-ink mt-3">Your trade profile</h1>
        <p className="text-sub mt-4 leading-relaxed">
          We don&apos;t resell or quote on your behalf — this profile is used only
          to match you against sanitized buyer requests. Editable any time.
        </p>

        <section className="mt-10 space-y-8">
          <div>
            <div className="overline mb-3">Commodities you supply</div>
            <Toggle value={form.commodities}
                    set={(v) => setForm({ ...form, commodities: v })}
                    options={COMMODITY_OPTIONS} testid="sp-commodities" />
          </div>

          <label className="block">
            <span className="overline block mb-2">Origins / refineries / mills</span>
            <textarea rows={2} className="input-line resize-none" value={form.origins}
                      onChange={set("origins")} required data-testid="sp-origins"
                      placeholder="e.g. Saras Sarroch refinery, IT · Centro Açúcar mill, Pernambuco BR" />
          </label>

          <label className="block">
            <span className="overline block mb-2">Monthly capacity</span>
            <textarea rows={2} className="input-line resize-none" value={form.monthly_capacity}
                      onChange={set("monthly_capacity")} required data-testid="sp-capacity"
                      placeholder="e.g. 250,000 MT EN-590 / month · 30,000 MT ICUMSA 45 / month" />
          </label>

          <label className="block">
            <span className="overline block mb-2">Quality specifications guaranteed</span>
            <textarea rows={2} className="input-line resize-none" value={form.quality_specs}
                      onChange={set("quality_specs")} required data-testid="sp-specs"
                      placeholder="e.g. EN-590 10ppm sulfur · ICUMSA 45 polarization ≥99.8°Z" />
          </label>

          <div>
            <div className="overline mb-3">Payment instruments accepted</div>
            <Toggle value={form.payment_terms_accepted}
                    set={(v) => setForm({ ...form, payment_terms_accepted: v })}
                    options={PAYMENT_OPTIONS} testid="sp-payment" />
          </div>

          <label className="block">
            <span className="overline block mb-2">Reference cargoes (prior deals)</span>
            <textarea rows={3} className="input-line resize-none" value={form.reference_cargoes}
                      onChange={set("reference_cargoes")} data-testid="sp-references"
                      placeholder="e.g. 50,000 MT EN-590 → Rotterdam, Q3 2025, MT Sirius · 12,500 MT ICUMSA 45 → Casablanca, Jun 2025" />
          </label>

          <label className="block">
            <span className="overline block mb-2">Primary banking institution</span>
            <input type="text" className="input-line" value={form.banking_institution}
                   onChange={set("banking_institution")} required data-testid="sp-bank"
                   placeholder="e.g. BNP Paribas, Geneva" />
          </label>
        </section>

        {err && <div className="mt-6 border-l-2 border-danger pl-3 text-sm text-danger" data-testid="sp-error">{err}</div>}

        <div className="mt-10">
          <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="sp-submit">
            {busy ? "Saving…" : "Save & continue to agreements →"}
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
}
